import { Edge } from "@xyflow/react";
import {
  Field,
  NODE_TYPE,
  RelativeTimeField,
  RFNodeType,
  WorkflowNodeData,
} from "../components/GraphBuilder/Nodes/utils/types";
import { NODE_TYPE_TO_CATEGORY } from "../components/GraphBuilder/Nodes/utils/constants";
import { DEFAULT_EDGE_TYPE } from "../components/GraphBuilder/Edges/edgeTypesMap";
import { v4 as uuidv4 } from "uuid";
import type { NormalizedGraph } from "./normalizedHelpers";

// ---------------------------------------------------------------------------
// DAG support helper -- use this instead of reading parentNodeId directly
// ---------------------------------------------------------------------------

export const getParentIds = (node: WorkflowNodeData): string[] => {
  if (node.parentNodeIds && node.parentNodeIds.length > 0) {
    return node.parentNodeIds;
  }
  return node.parentNodeId ? [node.parentNodeId] : [];
};

/**
 * BFS upward from `node`'s parents through `nodesById`, returning true if any
 * ancestor satisfies the predicate. Cycle-safe via visited set.
 */
export const findAncestor = (
  node: WorkflowNodeData,
  nodesById: Record<string, RFNodeType>,
  predicate: (data: WorkflowNodeData) => boolean,
): boolean => {
  const visited = new Set<string>();
  const queue: string[] = [...getParentIds(node)];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id || visited.has(id)) continue;
    visited.add(id);
    const parent = nodesById[id];
    if (!parent) continue;
    if (predicate(parent.data)) return true;
    queue.push(...getParentIds(parent.data));
  }
  return false;
};

/**
Creates a Default unsaved ReactFlow Node Data using journeyId and nodeType
@param nodeType - NODE_TYPE
@param journeyId - string
@param adjacentNodes - { parentNodeId?: string, childNodeIds?: string[] }
@param availableFields - Field[]
@param relativeTimeFields - RelativeTimeField[]
@returns WorkflowNodeData
**/
export const getDefaultNodeData = (
  nodeType: NODE_TYPE,
  journeyId: string,
  adjacentNodes?: {
    parentNodeId?: string;
    childNodeIds?: string[];
  },
  availableFields?: Field[],
  relativeTimeFields?: RelativeTimeField[]
): WorkflowNodeData => {
  return {
    id: uuidv4(),
    nodeCategory: NODE_TYPE_TO_CATEGORY[nodeType],
    nodeType: nodeType,
    journeyId: journeyId,
    formValues: {},
    parentNodeId: adjacentNodes?.parentNodeId ?? undefined,
    childNodeIds: adjacentNodes?.childNodeIds ?? undefined,
    availableFields: availableFields ?? [],
    relativeTimeFields: relativeTimeFields ?? [],
    stepNumber: 0,
    isSaved: false,
    isComplete: false,
    isLoading: false,
    markedInvalid: false,
  };
};

/**
Create a ReactFlow Edge from a source and target
@param source - string
@param target - string
@returns Edge
**/
export const createRFEdge = (source: string, target: string): Edge => {
  return {
    id: uuidv4(),
    source: source,
    target: target,
    type: DEFAULT_EDGE_TYPE,
  };
};

/**
Create a ReactFlow Node from a WorkflowNodeData
@param nodeType - NODE_TYPE
@param isLocal - boolean (true if node is local, false if node is already created through API call)
@param nodeData - WorkflowNodeData
@returns RFNodeType
**/
export const createRFNode = (
  nodeType: NODE_TYPE,
  isLocal: boolean,
  nodeData: WorkflowNodeData
): RFNodeType => {
  if (!isLocal) {

    const derivedIsConfigured =
      nodeData.isComplete ??
      Boolean(
        nodeData.formValues &&
          Object.keys(nodeData.formValues).length > 0,
      );
    return {
      id: nodeData.id,
      type: nodeData.nodeType,
      data: {
        ...nodeData,
        isSaved: true,
        isComplete: derivedIsConfigured,
        isLoading: false,
        markedInvalid: nodeData.markedInvalid ?? false,
      },
      position: { x: 0, y: 0 },
      draggable: false,
    };
  } else {
    const newNodeId = nodeData.id ?? uuidv4();
    return {
      id: newNodeId,
      type: nodeType,
      data: { ...nodeData, id: newNodeId },
      position: { x: 0, y: 0 },
      draggable: false,
    };
  }
};

const cloneNodeData = (
  graph: NormalizedGraph,
  nodeId: string,
  patch: Partial<WorkflowNodeData>
): NormalizedGraph => {
  const node = graph.nodesById[nodeId];
  if (!node) return graph;
  return {
    ...graph,
    nodesById: {
      ...graph.nodesById,
      [nodeId]: { ...node, data: { ...node.data, ...patch } },
    },
  };
};

/**
Append a new node as a leaf child of its parentNodeId.
- Adds newNode.id to parent's childNodeIds
- Inserts the node right after the parent in the array
- Creates a new edge from parent → newNode
**/
const __appendNodeAsLeaf = (
  newNode: RFNodeType,
  graph: NormalizedGraph
): NormalizedGraph => {
  const parentId = newNode.data.parentNodeId;
  if (!parentId || !graph.nodesById[parentId]) return graph;

  const parent = graph.nodesById[parentId];
  const updatedParent: RFNodeType = {
    ...parent,
    data: {
      ...parent.data,
      childNodeIds: [...(parent.data.childNodeIds ?? []), newNode.id],
    },
  };

  const newEdge = createRFEdge(parentId, newNode.id);
  const parentIdx = graph.nodeIds.indexOf(parentId);
  const newNodeIds = [...graph.nodeIds];
  newNodeIds.splice(parentIdx + 1, 0, newNode.id);

  return {
    nodesById: {
      ...graph.nodesById,
      [parentId]: updatedParent,
      [newNode.id]: newNode,
    },
    nodeIds: newNodeIds,
    edgesById: { ...graph.edgesById, [newEdge.id]: newEdge },
    edgeIds: [...graph.edgeIds, newEdge.id],
  };
};

/**
Splice a new node into an existing edge, preserving sibling order.
- Replaces childNodeId with newNode.id at the same index in parent's childNodeIds
- Updates the child's parentNodeId to newNode
- Inserts the node right after the parent in the array
- Replaces the old edge with parent to newNode, and inserts newNode to child edge
**/
const __spliceNodeIntoEdge = (
  newNode: RFNodeType,
  graph: NormalizedGraph,
  edgeId: string
): NormalizedGraph => {
  const edge = graph.edgesById[edgeId];
  if (!edge) return graph;

  const parentId = edge.source;
  const childId = edge.target;
  const parent = graph.nodesById[parentId];
  const child = graph.nodesById[childId];
  if (!parent || !child) return graph;

  const parentChildIds = [...(parent.data.childNodeIds ?? [])];
  const spliceIdx = parentChildIds.indexOf(childId);
  if (spliceIdx !== -1) {
    parentChildIds.splice(spliceIdx, 1, newNode.id);
  } else {
    parentChildIds.push(newNode.id);
  }

  const splicedNewNode: RFNodeType = {
    ...newNode,
    data: { ...newNode.data, childNodeIds: [childId] },
  };

  const parentIdx = graph.nodeIds.indexOf(parentId);
  const newNodeIds = [...graph.nodeIds];
  newNodeIds.splice(parentIdx + 1, 0, newNode.id);

  const updatedOldEdge: Edge = { ...edge, target: newNode.id };
  const newEdge = createRFEdge(newNode.id, childId);

  const newEdgeIds = [...graph.edgeIds];
  const oldEdgeIdx = newEdgeIds.indexOf(edgeId);
  newEdgeIds.splice(oldEdgeIdx + 1, 0, newEdge.id);

  return {
    nodesById: {
      ...graph.nodesById,
      [parentId]: {
        ...parent,
        data: { ...parent.data, childNodeIds: parentChildIds },
      },
      [childId]: {
        ...child,
        data: { ...child.data, parentNodeId: newNode.id },
      },
      [newNode.id]: splicedNewNode,
    },
    nodeIds: newNodeIds,
    edgesById: {
      ...graph.edgesById,
      [edgeId]: updatedOldEdge,
      [newEdge.id]: newEdge,
    },
    edgeIds: newEdgeIds,
  };
};

/**
Insert a node into the graph. Handles three scenarios:
1. Root insertion  — empty graph, no edgeId needed
2. Leaf insertion  — append as child of newNode.data.parentNodeId, no edgeId needed
3. Intermediate    — splice into an existing edge identified by edgeId
@param newNode - RFNodeType to insert
@param graph - NormalizedGraph
@param edgeId - only provided for intermediate insertion (scenario 3)
@returns NormalizedGraph
**/
export const insertNode = (
  newNode: RFNodeType,
  graph: NormalizedGraph,
  edgeId?: string
): NormalizedGraph => {
  if (graph.nodeIds.length === 0) {
    return {
      nodesById: { [newNode.id]: newNode },
      nodeIds: [newNode.id],
      edgesById: graph.edgesById,
      edgeIds: graph.edgeIds,
    };
  }
  if (edgeId) {
    return __spliceNodeIntoEdge(newNode, graph, edgeId);
  }
  return __appendNodeAsLeaf(newNode, graph);
};

/**
Replace a node created locally with API-backed data using previous id.
- Replaces node data and marks it as saved
- If API returns a new id, remaps edge source/target and parent/child references
**/
export const replaceNodeWithApiData = (
  previousId: string,
  apiNodeData: WorkflowNodeData,
  markAsSaved: boolean,
  graph: NormalizedGraph,
  selectedNodeId: string | null,
  markAsConfigured?: boolean
): { graph: NormalizedGraph; selectedNodeId: string | null } => {
  const existingNode = graph.nodesById[previousId];
  if (!existingNode) {
    return { graph, selectedNodeId };
  }

  const newId = apiNodeData.id;
  // Resolve the `isComplete` flag with the following precedence:
  // 1. Explicit `markAsConfigured` argument (used by save & exit / placement).
  // 2. The flag returned by the API (in case the BE starts emitting it).
  // 3. Derived from `formValues` -- empty means the node hasn't been configured.
  const resolvedIsConfigured =
    markAsConfigured ??
    apiNodeData.isComplete ??
    Boolean(
      apiNodeData.formValues &&
        Object.keys(apiNodeData.formValues).length > 0,
    );

  const updatedNode: RFNodeType = {
    ...existingNode,
    id: newId,
    type: apiNodeData.nodeType,
    data: {
      ...apiNodeData,
      isSaved: markAsSaved,
      isComplete: resolvedIsConfigured,
      isLoading: false,
      markedInvalid: false,
    },
  };

  const nodesById = { ...graph.nodesById };
  delete nodesById[previousId];
  nodesById[newId] = updatedNode;

  const nodeIds = graph.nodeIds.map((id) => (id === previousId ? newId : id));

  let updatedSelectedNodeId = selectedNodeId;

  if (newId !== previousId) {
    const edgesById = { ...graph.edgesById };
    for (const eid of graph.edgeIds) {
      const e = edgesById[eid];
      if (e.source === previousId || e.target === previousId) {
        edgesById[eid] = {
          ...e,
          source: e.source === previousId ? newId : e.source,
          target: e.target === previousId ? newId : e.target,
        };
      }
    }

    const parentId = existingNode.data.parentNodeId;
    if (parentId && nodesById[parentId]) {
      const p = nodesById[parentId];
      nodesById[parentId] = {
        ...p,
        data: {
          ...p.data,
          childNodeIds: p.data.childNodeIds?.map((c) =>
            c === previousId ? newId : c
          ),
        },
      };
    }
    for (const cid of existingNode.data.childNodeIds ?? []) {
      if (nodesById[cid]) {
        const c = nodesById[cid];
        nodesById[cid] = {
          ...c,
          data: {
            ...c.data,
            parentNodeId:
              c.data.parentNodeId === previousId ? newId : c.data.parentNodeId,
          },
        };
      }
    }

    if (updatedSelectedNodeId === previousId) {
      updatedSelectedNodeId = newId;
    }

    return {
      graph: { nodesById, nodeIds, edgesById, edgeIds: graph.edgeIds },
      selectedNodeId: updatedSelectedNodeId,
    };
  }

  return {
    graph: {
      nodesById,
      nodeIds,
      edgesById: graph.edgesById,
      edgeIds: graph.edgeIds,
    },
    selectedNodeId: updatedSelectedNodeId,
  };
};

// ---------------------------------------------------------------------------
// Prune the entire branch below the given nodeId including the node itself.
// Collects all descendant IDs via BFS and removes them along with their edges.
// ---------------------------------------------------------------------------

export const pruneBranchBelowNode = (
  nodeId: string,
  graph: NormalizedGraph
): NormalizedGraph => {
  const idsToRemove = new Set<string>();
  const queue = [nodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    idsToRemove.add(current);
    const node = graph.nodesById[current];
    if (node?.data.childNodeIds) {
      for (const childId of node.data.childNodeIds) {
        if (!idsToRemove.has(childId)) {
          queue.push(childId);
        }
      }
    }
  }

  const nodesById = { ...graph.nodesById };
  for (const id of idsToRemove) {
    delete nodesById[id];
  }

  const nodeIds = graph.nodeIds.filter((id) => !idsToRemove.has(id));

  const edgesById: Record<string, Edge> = {};
  const edgeIds: string[] = [];
  for (const eid of graph.edgeIds) {
    const e = graph.edgesById[eid];
    if (!idsToRemove.has(e.source) && !idsToRemove.has(e.target)) {
      edgesById[eid] = e;
      edgeIds.push(eid);
    }
  }

  return { nodesById, nodeIds, edgesById, edgeIds };
};

// ---------------------------------------------------------------------------
// Delete the node and stitch the adjacent nodes together.
// - Leaf node: remove node + its incoming edge, update parent's childNodeIds
// - Single child: re-parent child to this node's parent, stitch edges
// - Multiple children (branching): prune entire subtree
// - If node is a branch, delete the entire branch subtree
// ---------------------------------------------------------------------------

export const deleteNode = (
  nodeId: string,
  graph: NormalizedGraph,
  isBranch: boolean = false
): NormalizedGraph => {
  const node = graph.nodesById[nodeId];
  if (!node) return graph;

  const childIds = node.data.childNodeIds ?? [];
  const parentId = node.data.parentNodeId;

  if (childIds.length === 0) {
    // Case 1: Leaf node
    const nodesById = { ...graph.nodesById };
    delete nodesById[nodeId];
    const nodeIds = graph.nodeIds.filter((id) => id !== nodeId);

    const edgesById: Record<string, Edge> = {};
    const edgeIds: string[] = [];
    for (const eid of graph.edgeIds) {
      const e = graph.edgesById[eid];
      if (e.target !== nodeId) {
        edgesById[eid] = e;
        edgeIds.push(eid);
      }
    }

    if (parentId && nodesById[parentId]) {
      const p = nodesById[parentId];
      nodesById[parentId] = {
        ...p,
        data: {
          ...p.data,
          childNodeIds: (p.data.childNodeIds ?? []).filter(
            (id) => id !== nodeId
          ),
        },
      };
    }

    return { nodesById, nodeIds, edgesById, edgeIds };
  }

  if (childIds.length === 1 && !isBranch) {
    // Case 2: Single child -- stitch parent <-> child
    const childId = childIds[0];
    const nodesById = { ...graph.nodesById };
    delete nodesById[nodeId];
    const nodeIds = graph.nodeIds.filter((id) => id !== nodeId);

    const edgesById: Record<string, Edge> = {};
    const edgeIds: string[] = [];
    for (const eid of graph.edgeIds) {
      const e = graph.edgesById[eid];
      if (e.source !== nodeId && e.target !== nodeId) {
        edgesById[eid] = e;
        edgeIds.push(eid);
      }
    }

    if (nodesById[childId]) {
      const c = nodesById[childId];
      nodesById[childId] = {
        ...c,
        data: { ...c.data, parentNodeId: parentId },
      };
    }
    if (parentId && nodesById[parentId]) {
      const p = nodesById[parentId];
      nodesById[parentId] = {
        ...p,
        data: {
          ...p.data,
          childNodeIds: (p.data.childNodeIds ?? []).map((id) =>
            id === nodeId ? childId : id
          ),
        },
      };

      const stitchEdge = createRFEdge(parentId, childId);
      edgesById[stitchEdge.id] = stitchEdge;
      edgeIds.push(stitchEdge.id);
    }

    return { nodesById, nodeIds, edgesById, edgeIds };
  }

  // Case 3: Multiple children or branch -- prune entire subtree
  let result = pruneBranchBelowNode(nodeId, graph);

  if (parentId && result.nodesById[parentId]) {
    result = cloneNodeData(result, parentId, {
      childNodeIds: (result.nodesById[parentId].data.childNodeIds ?? []).filter(
        (id) => id !== nodeId
      ),
    });

    const edgesById = { ...result.edgesById };
    const edgeIds: string[] = [];
    for (const eid of result.edgeIds) {
      const e = edgesById[eid];
      if (e.source === parentId && e.target === nodeId) {
        delete edgesById[eid];
      } else {
        edgeIds.push(eid);
      }
    }
    result = { ...result, edgesById, edgeIds };
  }

  return result;
};
